import { useCallback, useLayoutEffect, useRef, useState } from "react";

// Scroll behavior constants
const BOTTOM_THRESHOLD = 50;
const PENDING_QUESTION_TOP_PADDING = 16;

/**
 * Options for the scroll management hook
 */
interface UseScrollManagementOptions {
  /** Whether the pending question should be pushed to the top */
  pushUpPendingQuestion?: boolean;
}

/**
 * Return type for the scroll management hook
 */
interface UseScrollManagementReturn {
  /** Ref to attach to the scrollable container */
  scrollRef: React.RefObject<HTMLDivElement | null>;
  /** Whether the user is scrolled to the bottom */
  isAtBottom: boolean;
  /** Height of the scroll container */
  scrollContainerHeight: number;
  /** Function to scroll to the bottom of the container */
  scrollToBottom: () => void;
  /** Function to scroll an element to the top with padding */
  scrollElementToTop: (_element: HTMLElement) => void;
}

/**
 * Custom hook for managing scroll behavior in the chat thread.
 * Provides utilities for auto-scrolling, position tracking, and container size monitoring.
 *
 * Features:
 * - Tracks whether user is at the bottom of the scroll container
 * - Provides smooth scrolling utilities using requestAnimationFrame
 * - Monitors container size changes with ResizeObserver
 * - Handles cleanup of all listeners and RAF calls
 *
 * @param options - Configuration options for scroll behavior
 * @returns Scroll management utilities and state
 *
 * @example
 * const { scrollRef, isAtBottom, scrollToBottom } = useScrollManagement({
 *   pushUpPendingQuestion: false
 * });
 *
 * return (
 *   <div ref={scrollRef}>
 *     {messages}
 *     {!isAtBottom && <ScrollToBottomButton onClick={scrollToBottom} />}
 *   </div>
 * );
 */
export function useScrollManagement(
  options: UseScrollManagementOptions = {}
): UseScrollManagementReturn {
  const { pushUpPendingQuestion = false } = options;

  // State
  const [isAtBottom, setIsAtBottom] = useState<boolean>(true);
  const [scrollContainerHeight, setScrollContainerHeight] = useState<number>(0);

  // Refs
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollRafIdRef = useRef<number | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  /**
   * Cancels any pending scroll animation frame
   */
  const cancelPendingScroll = useCallback(() => {
    if (scrollRafIdRef.current !== null) {
      cancelAnimationFrame(scrollRafIdRef.current);
      scrollRafIdRef.current = null;
    }
  }, []);

  /**
   * Scrolls the container to the bottom using RAF for smooth animation
   */
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    cancelPendingScroll();

    scrollRafIdRef.current = requestAnimationFrame(() => {
      scrollRafIdRef.current = null;
      el.scrollTop = el.scrollHeight;
    });
  }, [cancelPendingScroll]);

  /**
   * Scrolls an element to the top of the container with padding
   */
  const scrollElementToTop = useCallback(
    (element: HTMLElement) => {
      const container = scrollRef.current;
      if (!container) return;

      cancelPendingScroll();

      scrollRafIdRef.current = requestAnimationFrame(() => {
        scrollRafIdRef.current = null;
        const targetOffsetTop = element.offsetTop;
        container.scrollTop = targetOffsetTop - PENDING_QUESTION_TOP_PADDING;
      });
    },
    [cancelPendingScroll]
  );

  /**
   * Combined scroll position tracking and container size observation.
   * Handles: isAtBottom state, scroll container height, and ResizeObserver setup.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let rafId: number | null = null;

    // Update scroll container height
    const updateHeight = () => setScrollContainerHeight(el.clientHeight);
    updateHeight();

    // Check if user is near bottom
    const checkScrollPosition = () => {
      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        rafId = null;
        // When push-up mode is active, consider ourselves "at bottom"
        if (pushUpPendingQuestion) {
          setIsAtBottom(true);
          return;
        }
        const threshold = BOTTOM_THRESHOLD;
        const isNearBottom =
          el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
        setIsAtBottom(isNearBottom);
      });
    };

    // Set up ResizeObserver for container size changes
    const ro = new ResizeObserver(() => {
      updateHeight();
      checkScrollPosition();
    });
    ro.observe(el);
    resizeObserverRef.current = ro;

    // Set up scroll listener
    el.addEventListener("scroll", checkScrollPosition, { passive: true });
    checkScrollPosition(); // Check initial position

    return () => {
      el.removeEventListener("scroll", checkScrollPosition);
      if (rafId !== null) cancelAnimationFrame(rafId);
      ro.disconnect();
      resizeObserverRef.current = null;
    };
  }, [pushUpPendingQuestion]);

  /**
   * Cleanup on unmount - cancel any pending scroll operations
   */
  useLayoutEffect(() => {
    return () => {
      cancelPendingScroll();
    };
  }, [cancelPendingScroll]);

  return {
    scrollRef,
    isAtBottom,
    scrollContainerHeight,
    scrollToBottom,
    scrollElementToTop,
  };
}
